import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Col, Form, Modal, Row, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment from 'moment';
import { Controller, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import AppScrollArea from '../../components/common/AppScrollArea';
import CitySelector from '../../components/common/CitySelector';
import { useNotificationContext } from '../../providers/NotificationProvider';
import { useInitiateCustomerCreateApprovalMutation } from '../../slices/approvalsApiSlice';
import { useCheckCustomerNameMutation } from '../../slices/customersApiSlice';
import { useNotifyCustomerApplyToApplicantMutation, useNotifyCustomerApplyToApproverMutation } from '../../slices/notificationsApiSlice';
import { socket } from '../../socket';

const CUSTOMER_TYPES = ['科研', '工业', '临床', '机关'];
const DEFAULT_CITY = '310000'; // 默认城市代码：上海（GB/T 2260标准）
const SUBMIT_DEBOUNCE_TIME = 1500;
const EMPTY_CUSTOMER_APPLY_VALUES = {
	newCustomerType: '',
	newCustomerCity: DEFAULT_CITY,
	newCustomerName: '',
	newCustomerCompany: '',
	newCustomerDepartment: '',
	newCustomerEmail: '',
	newCustomerOrganization: '',
	newCustomerGroup: '',
	newCustomerPhone: '',
	newCustomerAddress: '',
};

const mergeCustomerApplyValues = (initialValues) => ({
	...EMPTY_CUSTOMER_APPLY_VALUES,
	...(initialValues || {}),
	newCustomerCity: initialValues?.newCustomerCity || DEFAULT_CITY,
});

const formatDateTime = (date) => (date ? moment(date).format('YYYY-MM-DD HH:mm') : '-');

const CustomerCreateApplyModal = ({
	setApplyModalShow,
	applyModalShow,
	applicant,
	initialValues,
	resubmittedFrom,
	onSubmitted,
	modalTitle,
	rejectionInfo,
	onCancelApplication,
}) => {
	const { loadingNotify, refetch } = useNotificationContext();
	const { userInfo } = useSelector((state) => state.auth);

	const [notifyApplicant] = useNotifyCustomerApplyToApplicantMutation();

	const [notifyAppover] = useNotifyCustomerApplyToApproverMutation();

	const [initiateCustomerCreateApproval, { isLoading: loadingApproval }] = useInitiateCustomerCreateApprovalMutation();

	const [checkCustomerName, { isLoading: nameChecking }] = useCheckCustomerNameMutation();

	// 防抖 / 防重复提交
	const [isSubmitting, setIsSubmitting] = useState(false);
	const isSubmittingRef = useRef(false);
	const submitDebounceTimer = useRef(null);

	const formDefaults = useMemo(() => mergeCustomerApplyValues(initialValues), [initialValues]);

	const {
		register,
		handleSubmit,
		formState: { errors },
		reset,
		control,
		watch,
		setValue,
	} = useForm({
		defaultValues: formDefaults,
	});
	const customerType = watch('newCustomerType');
	const previewName = `${(watch('newCustomerName') || '').trim()}${(watch('newCustomerDepartment') || '').trim()}${(watch('newCustomerGroup') || '').trim()}`;
	const modalHeading = modalTitle
		? modalTitle
		: resubmittedFrom
			? '再次申请添加客户'
			: userInfo?.role === 'assistant'
				? `为${applicant?.name || ''}申请添加客户`
				: '申请添加客户';
	const busy = loadingApproval || nameChecking || isSubmitting;

	const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
	const phonePattern = /^[\d\s\-()]+$/;

	useEffect(() => {
		if (!loadingNotify) {
			refetch();
		}
	}, [loadingNotify, refetch]);

	const openedRef = useRef(false);
	useEffect(() => {
		if (applyModalShow && !openedRef.current) {
			reset(formDefaults);
		}
		openedRef.current = Boolean(applyModalShow);
	}, [applyModalShow, formDefaults, reset]);

	const submitCustomerApproval = useCallback(
		async (data) => {
			// 防止重复提交
			if (isSubmittingRef.current) return;
			isSubmittingRef.current = true;
			setIsSubmitting(true);

			try {
				const newCustomerNameTrim = (data.newCustomerName || '').trim();
				const newCustomerDeptTrim = (data.newCustomerDepartment || '').trim();
				const newCustomerGroupTrim = (data.newCustomerGroup || '').trim();
				const newCustomerConcatName = `${newCustomerNameTrim}${newCustomerDeptTrim}${newCustomerGroupTrim}`;

				// 检查客户名称是否已存在
				await checkCustomerName({ newCustomerName: newCustomerConcatName }).unwrap();

				// 提交审批申请
				const res = await initiateCustomerCreateApproval({
					applicant,
					newCustomerType: data.newCustomerType,
					newCustomerConcatName,
					newCustomerName: newCustomerNameTrim,
					newCustomerOrganization: data.newCustomerCompany || '',
					newCustomerDepartment: newCustomerDeptTrim,
					newCustomerGroup: newCustomerGroupTrim,
					newCustomerPhone: (data.newCustomerPhone || '').trim(),
					newCustomerEmail: (data.newCustomerEmail || '').trim(),
					newCustomerCity: data.newCustomerCity,
					newCustomerAddress: (data.newCustomerAddress || '').trim(),
					...(resubmittedFrom ? { resubmittedFrom } : {}),
				}).unwrap();

				const { approver, assistant, detail, _id } = res;
				const notifyFrom = assistant || applicant?._id || userInfo?._id;

				// 发送通知
				if (assistant) {
					await notifyApplicant({ toUser: assistant, fromUser: notifyFrom, content: { detail, _id } }).unwrap();
				} else if (applicant?._id) {
					await notifyApplicant({ toUser: applicant._id, fromUser: notifyFrom, content: { detail, _id } }).unwrap();
				}
				if (approver) {
					await notifyAppover({ toUser: approver, fromUser: notifyFrom, content: { detail, _id } }).unwrap();
				}

				setApplyModalShow(false);
				reset(EMPTY_CUSTOMER_APPLY_VALUES);
				toast.success(resubmittedFrom ? '已再次提交，待批准' : '客户申请已提交，待批准');
				try {
					socket.emit('refresh processing orderlist');
				} catch {
					/* socket 未连接时忽略，依赖 mutation invalidate + 服务端广播 */
				}
				onSubmitted?.(res);
			} catch (error) {
				const errorMessage = error?.data?.message || error?.error || '提交失败，请稍后重试';
				toast.error(errorMessage);
			} finally {
				// 延迟重置提交状态，防止用户短时间内重复点击
				if (submitDebounceTimer.current) clearTimeout(submitDebounceTimer.current);
				submitDebounceTimer.current = setTimeout(() => {
					isSubmittingRef.current = false;
					setIsSubmitting(false);
				}, SUBMIT_DEBOUNCE_TIME);
			}
		},
		[
			applicant,
			checkCustomerName,
			initiateCustomerCreateApproval,
			notifyApplicant,
			notifyAppover,
			onSubmitted,
			reset,
			resubmittedFrom,
			setApplyModalShow,
			userInfo?._id,
		]
	);

	// 清理定时器
	useEffect(() => {
		return () => {
			if (submitDebounceTimer.current) clearTimeout(submitDebounceTimer.current);
		};
	}, []);

	const handleClose = useCallback(() => {
		setApplyModalShow(false);
		reset(EMPTY_CUSTOMER_APPLY_VALUES);
	}, [setApplyModalShow, reset]);

	return (
		<Modal show={applyModalShow} onHide={handleClose} backdrop='static' centered contentClassName='border product-form-modal' aria-labelledby='customer-create-apply-title'>
			<Form
				onSubmit={(e) => {
					e.preventDefault();
					e.stopPropagation();
					handleSubmit(submitCustomerApproval)(e);
				}}
				noValidate
			>
				<Modal.Header closeButton>
					<Modal.Title id='customer-create-apply-title' className='mb-0'>
						{modalHeading}
					</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<AppScrollArea maxHeight='70vh'>
						<input type='hidden' {...register('newCustomerCompany')} />
						{rejectionInfo ? (
							<div className='product-form-modal__section'>
								<div className='fs-10 fw-semibold text-danger mb-2'>审批驳回信息</div>
								<div className='fs-10'>
									<p className='mb-2'>
										<strong>审批人：</strong>
										{rejectionInfo.approverName || '-'}
									</p>
									<p className='mb-2'>
										<strong>驳回时间：</strong>
										{formatDateTime(rejectionInfo.rejectedAt)}
									</p>
									<p className='mb-0'>
										<strong>驳回原因：</strong>
										<span className='text-danger text-break'>{rejectionInfo.reason || '未填写驳回说明'}</span>
									</p>
								</div>
							</div>
						) : null}
						<div className='product-form-modal__section'>
							<div className='product-form-modal__section-title'>客户</div>
							<Form.Group className='product-form-modal__field' controlId='newCustomerType'>
								<Form.Label>
									客户类型 <span className='text-danger'>*</span>
								</Form.Label>
								<input type='hidden' {...register('newCustomerType', { required: '请选择客户类型' })} />
								<div className='d-flex gap-2'>
									{CUSTOMER_TYPES.map((type) => (
										<Button
											key={type}
											type='button'
											size='sm'
											className='flex-fill'
											variant={customerType === type ? 'primary' : 'outline-secondary'}
											onClick={() => setValue('newCustomerType', type, { shouldValidate: true })}
										>
											{type}
										</Button>
									))}
								</div>
								{errors.newCustomerType ? <div className='text-danger fs-10 mt-1'>{errors.newCustomerType.message}</div> : null}
							</Form.Group>
							<Form.Group className='product-form-modal__field' controlId='newCustomerName'>
								<Form.Label>
									单位名称 <span className='text-danger'>*</span>
								</Form.Label>
								<Form.Control
									type='text'
									placeholder='复旦大学'
									autoComplete='off'
									isInvalid={!!errors.newCustomerName}
									{...register('newCustomerName', {
										required: '请输入单位名称',
										minLength: { value: 2, message: '单位名称至少2个字符' },
									})}
								/>
								<Form.Control.Feedback type='invalid'>{errors.newCustomerName?.message}</Form.Control.Feedback>
								<Form.Text className='text-600'>与院系/部门、课题组连成客户名称，用于查重。</Form.Text>
							</Form.Group>
							<Row className='g-2 mx-0 product-form-modal__field'>
								<Col sm={6} className='ps-0'>
									<Form.Group controlId='newCustomerDepartment'>
										<Form.Label>院系/部门</Form.Label>
										<Form.Control type='text' placeholder='生命科学学院' autoComplete='off' {...register('newCustomerDepartment')} />
									</Form.Group>
								</Col>
								<Col sm={6} className='pe-0'>
									<Form.Group controlId='newCustomerGroup'>
										<Form.Label>课题组</Form.Label>
										<Form.Control type='text' placeholder='分子生物学课题组' autoComplete='off' {...register('newCustomerGroup')} />
									</Form.Group>
								</Col>
							</Row>
							{previewName ? (
								<div className='product-form-modal__field'>
									<div className='border rounded-3 px-3 py-2'>
										<div className='fs-11 text-600 mb-1'>客户名称</div>
										<div className='fw-semibold'>{previewName}</div>
									</div>
								</div>
							) : null}
						</div>
						<div className='product-form-modal__section'>
							<div className='product-form-modal__section-title'>地址</div>
							<Form.Group className='product-form-modal__field' controlId='newCustomerCity'>
								<Form.Label>
									城市 <span className='text-danger'>*</span>
								</Form.Label>
								<Controller
									name='newCustomerCity'
									control={control}
									rules={{ required: '请选择客户所在城市' }}
									render={({ field }) => (
										<CitySelector
											value={field.value || DEFAULT_CITY}
											onChange={(e) => {
												field.onChange(e.target.value);
											}}
											isInvalid={!!errors.newCustomerCity}
											size='medium'
										/>
									)}
								/>
								{errors.newCustomerCity ? <div className='text-danger fs-10 mt-1'>{errors.newCustomerCity.message}</div> : null}
							</Form.Group>
							<Form.Group className='product-form-modal__field' controlId='newCustomerAddress'>
								<Form.Label>
									详细地址 <span className='text-danger'>*</span>
								</Form.Label>
								<Form.Control
									as='textarea'
									rows={2}
									placeholder='杨浦区邯郸路220号'
									autoComplete='street-address'
									style={{ resize: 'vertical' }}
									isInvalid={!!errors.newCustomerAddress}
									{...register('newCustomerAddress', {
										required: '请输入客户详细地址',
										minLength: { value: 5, message: '详细地址至少5个字符' },
									})}
								/>
								<Form.Control.Feedback type='invalid'>{errors.newCustomerAddress?.message}</Form.Control.Feedback>
							</Form.Group>
						</div>
						<div className='product-form-modal__section'>
							<div className='product-form-modal__section-title'>联系</div>
							<Row className='g-2 mx-0 product-form-modal__field'>
								<Col sm={6} className='ps-0'>
									<Form.Group controlId='newCustomerPhone'>
										<Form.Label>单位电话</Form.Label>
										<Form.Control
											type='tel'
											placeholder='021-12345678'
											autoComplete='tel'
											isInvalid={!!errors.newCustomerPhone}
											{...register('newCustomerPhone', {
												pattern: {
													value: phonePattern,
													message: '请输入有效的电话号码',
												},
											})}
										/>
										<Form.Control.Feedback type='invalid'>{errors.newCustomerPhone?.message}</Form.Control.Feedback>
									</Form.Group>
								</Col>
								<Col sm={6} className='pe-0'>
									<Form.Group controlId='newCustomerEmail'>
										<Form.Label>邮箱</Form.Label>
										<Form.Control
											type='email'
											placeholder='zhang@example.com'
											autoComplete='email'
											isInvalid={!!errors.newCustomerEmail}
											{...register('newCustomerEmail', {
												pattern: {
													value: emailPattern,
													message: '请输入有效的邮箱地址',
												},
											})}
										/>
										<Form.Control.Feedback type='invalid'>{errors.newCustomerEmail?.message}</Form.Control.Feedback>
									</Form.Group>
								</Col>
							</Row>
						</div>
					</AppScrollArea>
				</Modal.Body>
				<Modal.Footer className='bg-body-tertiary'>
					<Button variant='falcon-default' onClick={handleClose} disabled={busy}>
						取消
					</Button>
					{onCancelApplication ? (
						<Button variant='falcon-default' type='button' onClick={onCancelApplication} disabled={busy}>
							<FontAwesomeIcon icon='ban' className='fs-11 me-2' />
							取消申请
						</Button>
					) : null}
					<Button variant='primary' type='submit' disabled={busy} className='d-inline-flex align-items-center'>
						{busy ? (
							<>
								<Spinner as='span' animation='border' size='sm' className='me-2' />
								<span>提交中...</span>
							</>
						) : (
							'提交申请'
						)}
					</Button>
				</Modal.Footer>
			</Form>
		</Modal>
	);
};

CustomerCreateApplyModal.propTypes = {
	setApplyModalShow: PropTypes.func.isRequired,
	applyModalShow: PropTypes.bool,
	applicant: PropTypes.object,
	initialValues: PropTypes.object,
	resubmittedFrom: PropTypes.string,
	onSubmitted: PropTypes.func,
	modalTitle: PropTypes.string,
	rejectionInfo: PropTypes.shape({
		approverName: PropTypes.string,
		rejectedAt: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
		reason: PropTypes.string,
	}),
	onCancelApplication: PropTypes.func,
};

export default CustomerCreateApplyModal;
